import React from 'react';
import { LegalLayout } from './LegalLayout';

export const Privacy: React.FC = () => {
  return (
    <LegalLayout
      kicker="Legal"
      title="Privacy Policy"
      lastUpdated="October 2, 2026"
      documentTitle="Privacy Policy — PaperBrain"
      intro="Plain-English version: your handwritten pages are private to you. We don't sell them, we don't train models on them, and you can export or delete them whenever you want. Below is the longer version."
    >
      <h2>1. What this policy covers</h2>
      <p>
        This Privacy Policy explains what information PaperBrain, operated by
        Byte-Sized Bits, LLC, collects, why, how it's used, and your choices. It
        applies to the PaperBrain web and mobile apps and the marketing site at
        paperbrain.dev.
      </p>

      <h2>2. Information we collect</h2>

      <h3>From your account</h3>
      <ul>
        <li>
          <strong>Email address</strong> — used to sign you in, send receipts,
          and contact you about your account.
        </li>
        <li>
          <strong>Hashed password</strong> — we store a one-way hash, never
          the password itself.
        </li>
        <li>
          <strong>Registration IP</strong> — recorded once at sign-up, used to
          limit abusive bulk-account creation.
        </li>
      </ul>

      <h3>From your usage</h3>
      <ul>
        <li>
          <strong>Pages you upload</strong> — original images plus the text we
          extract, your tags, and the collections you organize them into.
        </li>
        <li>
          <strong>Workspace conversations</strong> — questions you ask the AI
          workspace and the answers it returns, scoped to your library.
        </li>
        <li>
          <strong>Operational logs</strong> — basic request metadata
          (timestamps, status codes, error traces) needed to keep the service
          running and debug problems.
        </li>
      </ul>

      <h3>From payments</h3>
      <ul>
        <li>
          Purchases on paperbrain.dev are sold by <strong>Paddle</strong>, our
          reseller and Merchant of Record. Paddle collects your payment details
          and billing address directly and handles them under its own{' '}
          <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noreferrer">
            privacy policy
          </a>
          . We receive your subscription status, Paddle customer and
          subscription IDs, and billing period — <em>not</em> your card number.
        </li>
        <li>
          Purchases in the mobile apps are sold by <strong>Apple</strong> or{' '}
          <strong>Google</strong>. We receive the subscription status through{' '}
          <strong>RevenueCat</strong>, which tracks in-app purchases for us.
        </li>
      </ul>

      <h3>From your browser</h3>
      <ul>
        <li>
          We use a small amount of local browser storage to keep you signed in
          and to remember UI preferences. We don't use third-party advertising
          cookies.
        </li>
        <li>
          We load <strong>Google Ads conversion tracking</strong> (
          <code>gtag.js</code>) on the marketing site to measure ad
          performance. You can opt out via your browser's tracking-protection
          settings or an extension like uBlock Origin.
        </li>
      </ul>

      <h2>3. How we use your information</h2>
      <ul>
        <li>
          To run the service: storing pages, extracting text, answering
          workspace queries, syncing your library across devices.
        </li>
        <li>
          To bill you: Paddle (web) or Apple/Google (mobile) process payments
          and send receipts; we use the subscription status to unlock your
          account.
        </li>
        <li>
          To support you: responding to email, investigating issues, and
          recovering accounts.
        </li>
        <li>
          To keep things safe: detecting fraud, abuse, and bulk sign-up
          patterns.
        </li>
        <li>To comply with the law when we have to.</li>
      </ul>
      <p>
        We <strong>do not</strong> use your uploaded pages or workspace
        conversations to train AI models, and we don't sell your data to
        anyone.
      </p>

      <h2>4. Who we share information with</h2>
      <p>
        We share information only with service providers who need it to run
        PaperBrain on our behalf:
      </p>
      <ul>
        <li>
          <strong>OCR / AI providers</strong> — we send the image and a prompt
          so they can return the extracted text. They process it under their
          own security and data-handling policies and do not use it to train
          their models when accessed through their API.
        </li>
        <li>
          <strong>Paddle</strong> — our reseller for web purchases: payment,
          invoicing, tax, and the customer portal. We share your email and
          account ID so your purchase is linked to your account.
        </li>
        <li>
          <strong>RevenueCat</strong> — to confirm subscriptions bought in the
          mobile apps.
        </li>
        <li>
          <strong>Hosting &amp; infrastructure</strong> — providers that run
          our servers, databases, and file storage.
        </li>
        <li>
          <strong>Analytics &amp; ads</strong> — limited use of Google Ads
          tags on the marketing site only.
        </li>
      </ul>
      <p>
        We may also disclose information when required by law, to protect our
        rights or the safety of users, or in connection with a merger or sale
        of the business — in which case we'll tell you before your data is
        moved to a different controller.
      </p>

      <h2>5. How long we keep it</h2>
      <ul>
        <li>
          <strong>Your pages and account data</strong> stay until you delete
          them or close your account. After cancellation, we delete your
          Content within a reasonable period, except where retention is
          required by law.
        </li>
        <li>
          <strong>Operational logs</strong> are retained for a short window
          (typically 30–90 days) and then rotated out.
        </li>
        <li>
          <strong>Billing records</strong> may be retained for as long as tax
          and accounting laws require.
        </li>
      </ul>

      <h2>6. Your choices &amp; rights</h2>
      <ul>
        <li>
          <strong>Access &amp; export</strong> — you can export individual
          pages or whole collections as Markdown from the app at any time.
        </li>
        <li>
          <strong>Correction</strong> — you can update your email and password
          from the Account page.
        </li>
        <li>
          <strong>Deletion</strong> — delete pages individually, or contact us
          to delete your account entirely.
        </li>
        <li>
          <strong>Regional rights</strong> — depending on where you live (for
          example, the EU/UK or California), you may have additional rights to
          access, correct, port, or restrict the processing of your personal
          data. Email us and we'll help.
        </li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We use industry-standard practices: encrypted transport (HTTPS),
        password hashing, principle-of-least-privilege access for our team,
        and regular dependency updates. No system is perfectly secure — if we
        ever have a breach affecting your data, we'll notify you promptly.
      </p>

      <h2>8. Children</h2>
      <p>
        PaperBrain isn't directed at children under 13. We don't knowingly
        collect personal information from them. If you think a child has
        signed up, contact us and we'll remove the account.
      </p>

      <h2>9. International users</h2>
      <p>
        Our servers and providers may process data in countries other than
        your own. By using PaperBrain, you understand that your information
        may be transferred to and processed in jurisdictions with different
        data-protection laws.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We'll update this page when our practices change. Material changes
        will be announced in-app or by email. The "Last updated" date at the
        top tells you when this version was published.
      </p>

      <h2>11. Contact</h2>
      <div className="legal-contact">
        Questions, requests, or privacy concerns? Email{' '}
        <a href="mailto:hello@bytesizedbits.com">hello@bytesizedbits.com</a>.
      </div>
    </LegalLayout>
  );
};
